import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  FertilizerReportItemApi,
  FertilizerTable,
} from 'src/app/advice/models/fertilization-result.model';
import {
  selectExecutedTreatments,
  selectRecommendedNutrientQuantityWithIds,
} from 'src/app/advice/store/selectors';
import { CropFieldAdvice, ReportType } from 'src/app/shared/models';
import { ApiService } from 'src/app/shared/services/api.service';
import {
  getCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectLoggedInEmployeeAsArray,
} from 'src/app/store/selectors';

import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-fertilization-table-report',
  templateUrl: './fertilization-table-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class FertilizationTableReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  private readonly api = inject(ApiService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  reportType$ = this.store.pipe(select(RouterSelectors.getRouteParam('reportType')));
  isGBM$ = this.reportType$.pipe(
    map((reportType) => reportType === ReportType.GBMWithEnvironmentalImpactPoints),
  );

  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} ${this.locale.translate(
          'fertilizationTableReport',
        )} ${cropYear}`,
    ),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  fieldIds$ = this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldAdviceIds')));
  cropFieldAdvices$ = this.store.pipe(select(selectCropFieldAdvices));
  executedTreatments$ = this.store.pipe(select(selectExecutedTreatments));
  reportSort$ = this.store.pipe(select(getReportSort));
  tableReportData: FertilizerTable[] = [];
  sortedTableReportData$: BehaviorSubject<FertilizerTable[]> = new BehaviorSubject<
    FertilizerTable[]
  >([]);

  buttonItems$ = this.sortService.getButtonItems();

  private isTableDoneRendering = false;

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    combineLatest([
      this.store.pipe(select(selectExecutedTreatments)),
      this.cropFieldAdvices$,
      this.store.pipe(select(selectRecommendedNutrientQuantityWithIds)),
      this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldAdviceIds'))),
      this.cropYear$,
    ])
      .pipe(
        take(1),
        switchMap(
          ([
            executedTreatments,
            cropFieldAdvices,
            recommendedNutrientQuantity,
            adviceIdsString,
            cropYear,
          ]) => {
            const fieldIds = JSON.parse(adviceIdsString) as number[];
            const adviceIds = this.getAdviceIds(fieldIds, cropFieldAdvices);
            return this.api
              .getFertilizationReport(
                cropYear,
                executedTreatments,
                recommendedNutrientQuantity,
                adviceIds,
              )
              .pipe(map((result) => this.setData(this.mapApiResultToTableData(result))));
          },
        ),
      )
      .subscribe();
  }

  setData(tableData: FertilizerTable[]) {
    this.tableReportData = tableData;

    this.reportSort$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((reportSort) =>
        this.sortedTableReportData$.next(
          this.sortService.sort({
            elements: this.tableReportData,
            option: reportSort.option,
            descending: reportSort.descending,
            cropFieldNameFunc: (fertilizerTable) => fertilizerTable.metaData.name,
            cropNameFunc: (fertilizerTable) => fertilizerTable.metaData.cropName,
          }),
        ),
      );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getAdviceIds(fieldIds: number[], cropFieldAdvices: CropFieldAdvice[]) {
    const adviceIds: number[] = [];
    fieldIds.forEach((fieldId) => {
      const filteredCropFieldAdvices = cropFieldAdvices.filter((a) => a.cropFieldId === fieldId);
      const adviceId = filteredCropFieldAdvices.length
        ? filteredCropFieldAdvices[0].adviceId
        : null;
      if (adviceId) {
        adviceIds.push(adviceId);
      }
    });
    return adviceIds;
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    if (this.isTableDoneRendering) {
      this.sortService.handleButtonAction(data);
    }
  }

  updateIsDoneRendering(isTableDoneRendering: boolean) {
    this.isTableDoneRendering = isTableDoneRendering;
  }

  private mapApiResultToTableData(result: FertilizerReportItemApi[]) {
    return result.map((item) => ({
      metaData: {
        name: item.name,
        cropName: item.cropName,
        cropVariety: item.cropVariety,
        area: item.area,
      },
      fertilizationItems: item.fertilizationItems.map((fItem) => ({
        source: fItem.source,
        data: fItem.fertilizerData,
        primary: fItem.primary,
        isExecuted: fItem.isExecuted,
        note: fItem.note,
        displayDosage: fItem.displayDosage,
      })),
    }));
  }
}
